import React, { useState, useEffect } from "react"
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from "react-router-dom"
import { getMpsApplicationDetails } from "../../../firebaseFunctions/getMPSApplicationDetails"
import companyLogoDefault from "../../../assets/images/eventImages/companyLogo.jpg"
import flagIcon from "../../../assets/images/flag.svg"
import document_icon from "../../../assets/images/upload_document.svg"
import information_icon from "../../../assets/images/information-circle.svg"
import ModalLoadingState from "../../PopUpModal/ModalLoadingState"
import Question from "../../ui/QuestionComponent"
import Button from "../../ui/Button"
import ModalComponent from "../../MPS/EventRegistration/EventsModal"
import PopUpModal from "../../PopUpModal/PopUpModal"
import { updateMPSApplication } from "../../../firebaseFunctions/updateMPSApplication"
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/solid";
import alert from "../../../assets/images/alert-circle.svg"
import { notificationService } from "../../../services/NotificationService"
import { useAuth } from "../../../context/AuthContext"
import SocialMediaHandles from "../../SocialMediaHandles/SocialMediaHandles"


const MarketplaceSellerDetails = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false)
    const [applicationDetails, setApplicationDetails] = useState({})
    const [popUpModalType, setPopUpModalType] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { id } = useParams()
    const navigate = useNavigate()

    const [boothNumber, setBoothNumber] = useState(applicationDetails?.boothNumber || null)
    const [declineReason, setDeclineReason] = useState("")
    useEffect(() => {
        if (id) {
            const fetchPreFilledData = async () => {
                setIsLoading(true);
                try {
                    // Fetch event details using getEventDetails function
                    const applicationDetailsData = await getMpsApplicationDetails(id);
                    if (applicationDetailsData?.success) {
                        setApplicationDetails(applicationDetailsData?.data?.application)
                    } else {
                        console.log("Event details not found");
                    }
                } catch (error) {
                    console.log("Error fetching data:", error);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchPreFilledData();
        }
    }, [id]);
    const handleBoothChangeClick = () => {
        setPopUpModalType("change booth");
        setIsModalOpen(true)
    };

    const handleAssignSellerClick = () => {
        setPopUpModalType("approve seller prompt");
        setIsModalOpen(true);
    }

    const handleBoothChange = async () => {
        if (boothNumber) {
            setIsLoading(true);
            const data = {
                boothNumber: boothNumber
            }
            try {
                const result = await updateMPSApplication(data, id);
                if (result.success) {
                    try {
                        const applicationDetailsData = await getMpsApplicationDetails(id);
                        if (applicationDetailsData?.success) {
                            setApplicationDetails(applicationDetailsData?.data?.application)
                        }
                    } catch (error) {
                        console.log("Error fetching updated data:", error);
                    }
                    setPopUpModalType("change booth success");
                    // Notify vendor of booth assignment via email + SMS
                    notificationService.sendBoothAssignedEmail({
                        recipientEmail: applicationDetails?.businessEmail,
                        vendorName: applicationDetails?.businessName,
                        eventName: applicationDetails?.eventName || '',
                        eventDate: applicationDetails?.eventDate || '',
                        boothNumber: boothNumber,
                        boothLocation: '',
                        organizerName: '',
                    }).catch(() => {});
                    if (applicationDetails?.businessPhoneNumber) {
                        notificationService.sendBoothAssignedSMS({
                            phoneNumber: applicationDetails.businessPhoneNumber,
                            vendorName: applicationDetails.businessName,
                            eventName: applicationDetails?.eventName || '',
                            boothNumber: boothNumber,
                        });
                    }
                } else {
                    setPopUpModalType("change booth failure");
                }
            } catch (error) {
                console.error("Error updating application:", error);
                setPopUpModalType("change booth failure");
            } finally {
                setIsLoading(false);
            }
        }
    };
    const handleApproveSeller = async (status, nextModal) => {
        setIsLoading(true);
        const data = {
            applicationStatus: status,
            ...(status === "Declined" && declineReason.trim() ? { declineReason: declineReason.trim() } : {}),
        }
        try {
            const result = await updateMPSApplication(data, id);
            if (result.success) {
                try {
                    const applicationDetailsData = await getMpsApplicationDetails(id);
                    if (applicationDetailsData?.success) {
                        const appData = applicationDetailsData?.data?.application;
                        setApplicationDetails(appData);
                        setPopUpModalType(nextModal);
                        
                        if (status === "Approved") {
                            notificationService.sendApplicationApprovalEmail({
                                mpsEmail: appData.mpsEmail,
                                businessName: appData.businessName || appData.mpsBusinessName,
                                eventName: appData.eventName,
                                eventDate: appData.eventDate,
                                registrationFee: appData.registrationFee,
                                applicationId: id,
                                organizerName: user?.displayName || "Event Organizer",
                                eoUserId: user?.uid
                            }).catch(err => console.log("Email notification error:", err));
                            // SMS notification
                            if (appData?.businessPhoneNumber) {
                                notificationService.sendApplicationApprovedSMS({
                                    phoneNumber: appData.businessPhoneNumber,
                                    vendorName: appData.businessName,
                                    eventName: appData.eventName || '',
                                    registrationFee: appData.registrationFee,
                                });
                            }
                        } else if (status === "Declined") {
                            notificationService.sendApplicationDenialEmail({
                                mpsEmail: appData.mpsEmail,
                                businessName: appData.businessName || appData.mpsBusinessName,
                                eventName: appData.eventName,
                                applicationId: id,
                                organizerName: user?.displayName || "Event Organizer",
                                eoUserId: user?.uid,
                                declineReason: declineReason.trim() || null,
                            }).catch(err => console.log("Email notification error:", err));
                            // No SMS for denials — email only (more context needed)
                        }
                    } else {
                        console.log("Event details not found, but approval was successful");
                        setPopUpModalType(nextModal);
                    }
                } catch (error) {
                    console.log("Error fetching updated data:", error);
                    setPopUpModalType(nextModal);
                }
            } else {
                console.error("Failed to update application:", result.message);
                setPopUpModalType("approve seller failure");
            }
        } catch (error) {
            console.error("Error updating application:", error);
            setPopUpModalType("approve seller failure");
        } finally {
            setIsLoading(false);
        }
    };



    const renderModals = () => {
        switch (popUpModalType) {
            case "change booth":
                return (
                    <ModalComponent
                        title={t('marketplaceDetails.changeBoothTitle')}
                        secondButtonLabel={t('settings.goBack')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('mpsDetails.changeBooth')}
                        firstButtonDisabled={!boothNumber}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        onFirstButtonClick={handleBoothChange}
                    >
                        <div className="w-full mt-6">
                            <p className="text-sm text-start font-medium">{t('marketplaceDetails.assignBoothNumber')}</p>
                            <input
                                type="number"
                                value={boothNumber}
                                onChange={(e) => setBoothNumber(e.target.value)}
                                placeholder={t('marketplaceDetails.assignBoothNumber')}
                                className="w-full p-3 border border-grey-300 rounded-lg mt-4"
                            />
                        </div>
                    </ModalComponent>
                );
            case "change booth success":
                return (
                    <ModalComponent
                        icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
                        title={t('marketplaceDetails.boothAssignedTitle')}
                        firstButtonLabel={t('common.continue')}
                        onFirstButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                    />
                );
            case "change booth failure":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('marketplaceDetails.errorChangingBooth')}
                        message={t('marketplaceDetails.pleaseTryAgain')}
                        secondButtonLabel={t('marketplaceDetails.changeLater')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('settings.tryAgain')}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        onFirstButtonClick={handleBoothChange}
                    />
                );
            case "approve seller prompt":
                return (
                    <ModalComponent
                        title={t('marketplaceDetails.approveSellerTitle')}
                        message={t('marketplaceDetails.approveSellerMessage')}
                        secondButtonLabel={t('marketplaceDetails.noGoBack')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('marketplaceDetails.approveSeller')}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        onFirstButtonClick={() => handleApproveSeller("Approved", "approve seller success")}
                    />
                );
            case "approve seller success":
                return (
                    <ModalComponent
                        icon={<CheckCircleIconSolid className="size-20 text-Success/700" />}
                        message={t('marketplaceDetails.assignBoothQuestion')}
                        secondButtonLabel={t('marketplaceDetails.assignLater')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('marketplaceDetails.assignBooth')}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        onFirstButtonClick={() => setPopUpModalType("change booth")}
                    />
                );
            case "approve seller failure":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('marketplaceDetails.errorApprovingTitle')}
                        message={t('marketplaceDetails.somethingWentWrong')}
                        secondButtonLabel={t('settings.goBack')}
                        secondButtonVariant="transparent"
                        firstButtonLabel={t('settings.tryAgain')}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); }}
                        onFirstButtonClick={() => handleApproveSeller("Approved", "approve seller success")}
                    />
                );

            case "revoke application prompt":
                return (
                    <ModalComponent
                        icon={<img src={alert} alt="alert" className="size-20" />}
                        title={t('marketplaceDetails.revokeApplicationTitle')}
                        message={t('marketplaceDetails.assignBoothQuestion')}
                        secondButtonLabel={t('marketplaceDetails.noGoBack')}
                        checkboxLabel={t('marketplaceDetails.refundFees')}
                        firstButtonVariant="transparent"
                        firstButtonLabel={t('marketplaceDetails.yesRevoke')}
                        onSecondButtonClick={() => { setPopUpModalType(null); setIsModalOpen(false); setDeclineReason(""); }}
                        onFirstButtonClick={() => handleApproveSeller("Declined", "revoke application success")}
                    >
                        <div className="w-full mt-4">
                            <label className="block text-sm font-medium text-grey-700 mb-1.5">
                                Reason for declining <span className="text-grey-400 font-normal">(optional — sent to vendor)</span>
                            </label>
                            <textarea
                                value={declineReason}
                                onChange={e => setDeclineReason(e.target.value)}
                                placeholder="e.g. We've reached capacity for your category…"
                                rows={3}
                                className="w-full px-3 py-2.5 border border-grey-300 rounded-lg text-sm text-grey-900 placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent resize-none"
                            />
                        </div>
                    </ModalComponent>
                );
            case "revoke application success":
                return (
                    <ModalComponent
                        title={t('marketplaceDetails.applicationRevoked')}
                        firstButtonLabel={t('marketplaceDetails.returnToSellerList')}
                        onFirstButtonClick={() => navigate("/eo/marketplace")}
                    />
                );
            default:
                return <ModalLoadingState />;
        }
    }
    return (
        <>
            <PopUpModal
                open={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
                popUpModalType={popUpModalType}
            >
                {!isLoading ? renderModals() : <ModalLoadingState />}
            </PopUpModal>

            <div className="p-6 px-8">
                <h2 className="font-[500] text-[24px] text-grey-900">{applicationDetails?.businessName}</h2>
                <p className="pt-6 text-md text-grey-900 font-medium">{t('settings.companyLogo')}</p>
                <img
                    src={applicationDetails?.companyLogoURL || companyLogoDefault}
                    className="rounded-xl h-[104px] w-[104px] mt-2"
                    alt=""
                />

                {/*business details*/}
                <div className="w-full pt-12 p-2 grid grid-cols-3 gap-4">
                    <div>
                        <DetailItem label={t('mpsDetails.businessName')} value={applicationDetails?.businessName} />
                        <DetailItem
                            label={t('mpsDetails.primaryContact')}
                            value={applicationDetails?.primaryContactName}
                        />
                        <DetailItem label={t('mpsDetails.emailAddress')} value={applicationDetails?.businessEmail} />
                    </div>
                    <div>
                        <DetailItem label={t('mpsDetails.businessType')} value={applicationDetails?.businessType} />
                        <DetailItem
                            label={t('mpsDetails.businessPhone')}
                            value={applicationDetails?.businessPhoneNumber}
                        />
                        <DetailItem
                            label={t('mpsDetails.smsContact')}
                            value={applicationDetails?.businessPhoneNumber}
                        />
                    </div>
                    <div className="pt-12">
                        <p className="text-md text-grey-900 font-medium inline-flex">
                            <span>
                                <img
                                    src={flagIcon}
                                    className="h-[19.5px] w-[16.5px] mx-2"
                                    alt=""
                                />
                            </span>
                            {t('mpsDetails.assignedBooth')}
                        </p>
                        <p className="pt-1 text-sm text-grey-700 font-normal ml-10">
                            {applicationDetails?.boothNumber || "-"}
                        </p>
                        <button
                            className="max-w-[158px] justify-center rounded-md px-3 py-2 mt-4 text-xs font-semibold transition duration-150 ease-in-out bg-grey-700 text-base-white hover:bg-primary-700"
                            onClick={handleBoothChangeClick}
                        >
                            {t('mpsDetails.changeBooth')}
                        </button>
                    </div>
                </div>
                {/*about*/}
                {applicationDetails?.businessDescription && <div className="w-full pt-6">
                    <div className="w-3/4 border-t border-gray-300 my-4" />
                    <p className="text-md text-grey-900 font-medium pt-4">
                        {t('eventDetails.aboutLabel')}
                    </p>
                    <p className="py-2 text-sm text-grey-700 font-normal">
                        {applicationDetails?.businessDescription}
                    </p>
                    <div className="w-3/4 border-t border-gray-300 my-4" />
                </div>}

                {/*business documents*/}
                <div className="w-full pt-6">
                    <p className="text-md text-grey-900 font-medium pt-4">
                        {t('mpsDetails.businessDocuments')}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 mt-4">
                        {applicationDetails?.businessDocuments?.map((document, index) => (
                            <a href={document.file} target="_blank" rel="noreferrer" key={index} className="border border-grey-300 rounded-2xl p-8 flex flex-col items-center justify-center">
                                <img
                                    src={document_icon}
                                    alt=""
                                    className="h-[40px] w-[40px]"
                                />
                                <p className="pt-2 text-sm font-medium text-success-700 whitespace-nowrap">
                                    {document?.fileName?.length > 15
                                        ? `${document?.fileName.slice(0, 15)}...`
                                        : document?.fileName}
                                </p>
                            </a>
                        ))}
                    </div>
                </div>

                {/*website and socials*/}
                <div className="w-1/2 pt-6 grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-md text-grey-900 font-medium pt-4">
                            {t('eventDetails.websiteLabel')}
                        </p>
                        {applicationDetails?.businessWebsite ? (
                            <a
                                href={applicationDetails.businessWebsite.startsWith('http') ? applicationDetails.businessWebsite : `https://${applicationDetails.businessWebsite}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="pt-1 text-sm text-primary-600 hover:text-primary-700 underline underline-offset-2 font-normal block truncate"
                            >
                                {applicationDetails.businessWebsite}
                            </a>
                        ) : (
                            <p className="pt-1 text-sm text-grey-400 font-normal">—</p>
                        )}
                    </div>
                    <div>
                        <p className="text-md text-grey-900 font-medium pt-4">
                            {t('eventDetails.socialMediaLabel')}
                        </p>
                        {applicationDetails?.businessInstagramAccount || applicationDetails?.businessFacebookAccount || applicationDetails?.businessTikTokAccount ? (
                            <div className="pt-1">
                                <SocialMediaHandles handles={{
                                    businessInstagramAccount: applicationDetails?.businessInstagramAccount,
                                    businessFacebookAccount: applicationDetails?.businessFacebookAccount,
                                    businessTikTokAccount: applicationDetails?.businessTikTokAccount,
                                }} />
                            </div>
                        ) : (
                            <p className="pt-1 text-sm text-grey-400 font-normal">—</p>
                        )}
                    </div>
                </div>

                {/*questions*/}
                <div className="pt-6">
                    <p className="text-xl text-grey-900 font-medium">
                        {t('eventDetails.questionsForVendors')}
                    </p>
                    {applicationDetails?.MPSanswers?.map((question, index) => (
                        <Question question={question} key={index} isAnswer={true} />
                    ))}

                </div>

                {/*controls*/}
                <div className="w-full">
                    <div className="flex items-center justify-end gap-4">
                        <p className="text-sm text-grey-700 font-normal inline-flex items-center gap-x-2">
                            {t('marketplaceDetails.waiveRegistrationFee')}
                            <img
                                src={information_icon}
                                alt="Information"
                                className="h-4 w-4 cursor-pointer"
                            />
                        </p>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" />
                            <div className="w-12 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primary-700 transition duration-300"></div>
                            <div className="absolute w-4 h-4 bg-white rounded-full shadow left-1 top-1 peer-checked:translate-x-6 transition-transform duration-300"></div>
                        </label>
                    </div>
                    {applicationDetails?.applicationStatus !== "Declined" && <div className="flex items-center justify-end gap-4 pt-4">
                        {applicationDetails?.applicationStatus === "Registered" ? <>
                            <Button
                                variant="underline"
                                onClick={() => { setPopUpModalType("revoke application prompt"); setIsModalOpen(true) }}
                            >
                                {t('marketplaceDetails.revokeApplication')}
                            </Button>
                            <Button onClick={() => navigate("/eo/marketplace")}>
                                {t('marketplaceDetails.returnToSellerList')}
                            </Button>
                        </> : <>
                            <Button
                                variant="underline"
                                onClick={() => { setPopUpModalType("revoke application prompt"); setIsModalOpen(true); }}
                            >
                                {t('marketplaceDetails.denyApplication')}
                            </Button>
                            <Button variant="transparent" onClick={() => navigate("/eo/marketplace")}>
                                {t('marketplaceDetails.returnToSellerList')}
                            </Button>
                            <Button
                                onClick={handleAssignSellerClick}
                            >
                                {t('marketplaceDetails.approveSeller')}
                            </Button>
                        </>}
                    </div>}
                </div>

            </div>
        </>
    )
}

export default MarketplaceSellerDetails


const DetailItem = ({ label, value }) => (
    <div className="pt-8">
        <p className="text-md text-grey-900 font-medium">{label}</p>
        <p className="pt-1 text-sm text-grey-700 font-normal">{value || "N/A"}</p>
    </div>
);



